// Conversion-focused landing page content (Elite Vending)
import { images } from "../mock";

export const freeVendingContent = {
  slug: "free-vending-machines-for-businesses",
  source: "landing-free",
  seo: {
    title: "Free Vending Machines for Businesses | Elite Vending",
    description:
      "Get a brand-new vending machine installed free at your workplace. Fully stocked, fully serviced, no contracts. Book a free site visit today."
  },
  hero: {
    badge: "Zero-Cost Install",
    titlePre: "Free Vending Machines for ",
    titleGold: "Busy Workplaces",
    subtitle:
      "Give your team snacks and drinks on tap — at no cost to you. We install, stock and service a brand-new machine for qualifying sites with 30+ staff or steady foot traffic.",
    bullets: [
      "$0 to install — nothing to rent, ever",
      "Fully stocked & serviced by us",
      "Tap & Go, cards, cash & coin",
      "No lock-in contracts"
    ],
    formTitle: "Claim your free machine",
    formSubtitle: "Book a free site visit — we'll confirm eligibility within one business day.",
    image: images.heroMachine
  },
  intro: {
    title: "How is it free?",
    body:
      "Simple: the machine pays for itself through the products people buy from it. Because we handle everything at scale — delivery, stocking, servicing and monitoring — a well-placed machine covers its own costs. That's why the only thing that matters is foot traffic, not a fee."
  },
  benefitsTitle: "Everything included, nothing to pay",
  benefits: [
    { icon: "Sparkles", title: "Brand-new machine", desc: "A sleek, modern unit that looks right in any professional space." },
    { icon: "CreditCard", title: "Every payment type", desc: "Tap & Go, cards, phones, watches — plus notes and coins." },
    { icon: "Wrench", title: "Stocked & serviced", desc: "We keep it clean, working and full — you never lift a finger." },
    { icon: "Radio", title: "Smart monitoring", desc: "Stock levels are tracked so refills happen before you run dry." },
    { icon: "ShieldCheck", title: "Safety tested", desc: "Electrically tested and compliant from the moment it lands." },
    { icon: "BadgeCheck", title: "Risk-free trial", desc: "Try it for 4 weeks. Not a fit? We simply collect it — no fuss." }
  ],
  steps: [
    { n: 1, title: "Book a free visit", desc: "We check your space and confirm you qualify — usually in minutes." },
    { n: 2, title: "Pick your products", desc: "Choose the drinks, snacks and healthy options your team will love." },
    { n: 3, title: "We install free", desc: "Delivered, tested and ready to use — often within days." },
    { n: 4, title: "We keep it full", desc: "Automatic monitoring means it's always stocked and serviced." }
  ],
  faqs: [
    { q: "Is it really free?", a: "Yes. For qualifying sites there's no purchase price, no rental, no service fee and no contract. The machine earns its keep through the products people buy." },
    { q: "Do we qualify?", a: "Most workplaces with 30+ staff or steady foot traffic qualify. Fewer than 30? It's still worth asking — site type and visitor numbers can qualify you too." },
    { q: "What does it cost to run?", a: "Power usage is roughly $100 a year on a standard outlet. That's the only overhead — and many sites feel it's well worth it." },
    { q: "What if it doesn't work out?", a: "There's no lock-in. If it's under-used or not a fit, we collect the machine with no exit fee." }
  ],
  testimonials: [
    { quote: "Installed in three days and our warehouse team hasn't stopped using it. Genuinely zero cost to us.", name: "Marcus T.", role: "Operations Manager" },
    { quote: "The healthy options were a hit with staff. It's always stocked and spotless.", name: "Priya S.", role: "Office Manager" }
  ],
  finalCta: {
    title: "Ready for a free vending machine?",
    subtitle: "Book your free site visit today — no cost, no obligation, no contracts."
  }
};

export const buyVendingContent = {
  slug: "buy-a-vending-machine",
  source: "landing-buy",
  seo: {
    title: "Buy a Vending Machine | Elite Vending",
    description:
      "Looking to buy a vending machine? Compare buying outright vs our free managed placement. Get expert advice and a tailored quote — enquire today."
  },
  hero: {
    badge: "Buy or Place Free",
    titlePre: "Buy a Vending Machine ",
    titleGold: "the Smart Way",
    subtitle:
      "Before you spend thousands on a machine, talk to us. Many businesses discover they can get a modern machine placed and managed for free — but if you'd rather own one outright, we'll set you up with the right unit and support.",
    bullets: [
      "Compare buying vs free placement",
      "Modern machines with Tap & Go",
      "Expert, no-pressure advice",
      "Tailored quote for your site"
    ],
    formTitle: "Get your tailored quote",
    formSubtitle: "Tell us about your site and we'll recommend the best option for you.",
    image: images.heroMachine
  },
  intro: {
    title: "Buy outright, or get it free?",
    body:
      "Buying a machine means an upfront cost plus ongoing stocking and servicing. For many workplaces, our free managed placement delivers the same modern machine with none of that hassle. We'll show you both options honestly so you can choose what's right for your business."
  },
  benefitsTitle: "Why buy through Elite Vending",
  benefits: [
    { icon: "Sparkles", title: "Modern hardware", desc: "Latest-generation machines with bright displays and reliable mechanics." },
    { icon: "CreditCard", title: "Cashless ready", desc: "Tap & Go and card payments built in as standard." },
    { icon: "LayoutGrid", title: "Configured for you", desc: "Snacks, drinks, chilled, combo — set up for your space and audience." },
    { icon: "Wrench", title: "Servicing support", desc: "Optional maintenance and stocking plans if you'd rather not DIY." },
    { icon: "Radio", title: "Monitoring add-on", desc: "See stock and sales data remotely so you never miss a refill." },
    { icon: "ShieldCheck", title: "Warranty & compliance", desc: "Safety tested and backed — peace of mind from day one." }
  ],
  steps: [
    { n: 1, title: "Tell us your goals", desc: "Owning, placing, or not sure yet? We start with what you want." },
    { n: 2, title: "Free site visit", desc: "We assess space, access and expected usage at no cost." },
    { n: 3, title: "Compare options", desc: "A clear, honest comparison of buying vs free managed placement." },
    { n: 4, title: "Get set up", desc: "Whichever you choose, we deliver, install and get you running." }
  ],
  faqs: [
    { q: "How much does a vending machine cost to buy?", a: "New machines vary widely by type and features. We'll give you a transparent quote — and show you whether free placement could save you the upfront spend entirely." },
    { q: "Should I buy or get one placed free?", a: "If you have 30+ staff or steady foot traffic, free managed placement often makes more sense. Buying suits sites that want full control and keep all sales revenue." },
    { q: "Do you help with stocking and servicing?", a: "Yes. Even if you buy, we offer optional stocking, servicing and monitoring plans so you're never left managing it alone." },
    { q: "How fast can I get one?", a: "After your site visit we can usually have a machine installed within days." }
  ],
  testimonials: [
    { quote: "We came in to buy and left with a free managed machine instead. Saved us thousands upfront.", name: "Daniel K.", role: "Facilities Lead" },
    { quote: "Honest advice and a great machine. The quote was clear with no hidden extras.", name: "Elena R.", role: "Gym Owner" }
  ],
  finalCta: {
    title: "Get the right machine for your business",
    subtitle: "Buy outright or place one free — book a free site visit and we'll help you decide."
  }
};
